import c from '../../../util/const'
import log from '../../../util/log'
import React, {Component} from 'react';
import {connect} from "react-redux";
import _ from 'lodash'
import PropTypes from 'prop-types';
import PdfPage from "./PdfPage";
import Promise from 'bluebird'
import {withVFTranslation} from "../../../util/withVFTranslation";
import util from "../../../util/util";

const pdfjsLib = await import("pdfjs-dist/legacy/build/pdf.min.mjs");

class PdfPrintPreviewSvc extends Component {

  pageSizes = {};
  canvasRefs = {};

  constructor(props){
    super(props);

    this.state = {
      showing : false,
      pdf : null,
      pages : []
    }
  }

  componentDidMount() {
    if(this.props.onRef) {
      this.props.onRef(this)
    }
  }

  componentWillUnmount() {
    if(this.props.onRef) {
      this.props.onRef(undefined);
    }
    this.cleanup();
  }

  cleanup(){
    if(this.state.pdf){
      this.state.pdf.destroy();
    }

    this.setState({
      showing: false,
      pages: [],
      pdf: null,
    })
    this.pageSizes = {};
    this.canvasRefs = {};
  }

  loadAndRender(data) {
    let init = {
      data: data,
      cMapUrl: c.pdf.cmapPath,
      cMapPacked: true,
      isEvalSupported: false, //our csp does not allow this.
      wasmUrl: c.pdf.wasmPath,
      iccUrl: c.pdf.iccsPath,
      standardFontDataUrl : c.pdf.standardFontsPath,
      enableXfa: true,
    }
    pdfjsLib.GlobalWorkerOptions.workerSrc = c.pdf.pdfjs_workerPath;
    return pdfjsLib.getDocument(init).promise
      .then((pdf) => {

        //We don't really do anything with this, except to destroy it later.
        this.setState({pdf})

        let calls = [];
        for (let num = 1; num <= pdf.numPages; num++) {
          calls.push(
            pdf.getPage(num)
              .then((page) => {
                page.pageIndex = page._pageIndex
                return page;
              })
          );
        }
        return Promise.all(calls);
      })
      .then((pageRes) => {
        return new Promise((resolve) => {
          _.each(pageRes, (page) => {
            this.initializePageSize(page);
          })

          //bump this setState out just a bit to make sure initializePageSize has been fully set
          //in all cases.
          this.setState({pages: pageRes},
            () => setTimeout(() => resolve(true), 100));
        })
      })
      .then(() => {
        return this.renderAllPages();
      })
  }

  renderAllPages() {
    return new Promise((resolve, reject) => {
      this.setState({showing: true}, () => {
        return this.waitForCanvasRefs()
          .then(() => {
            let pageRenders = [];
            _.each(this.state.pages, (page) => {
              pageRenders.push(this.renderPage(page.pageIndex))
            })
            return Promise.all(pageRenders)
          })
          .then(() => {
            resolve(true);
          })
          .catch((err) => {
            log.warn('error rendering pages', err);
            reject(err);
          })
      })
    })

  }

  getPage(index){
    let { pages } = this.state;

    let found = null;
    _.each(pages, (page) => {
      if(page.pageIndex === index){
        found = page;
      }
    })
    return found;
  }

  calculateViewportScale(outputScale){
    //this is kind of a weird hack.  I'm trying to calculate a scale for this page that is larger than
    //the render size.  The css here is designed to scale it appropriately if it is.
    //The trick is that we don't want to scale the viewport too larger because it's a performance problem.
    //At higher pixel ratios we're able to get away with a lower scaling, so lower the scale as the pixel ratio goes up.
    return 2 / outputScale.sx;
  }

  initializePageSize(page){
    let { sharedCanvasRef } = this.props;
    let ctx = sharedCanvasRef.current.getContext('2d', {alpha: false});
    let outputScale = PdfPage.getOutputScale(ctx);
    let viewport = page.getViewport({scale: 1}).clone({ scale : this.calculateViewportScale(outputScale) })
    this.pageSizes[page.pageIndex] = {
      canvasHeight : viewport.height * outputScale.sy | 0,
      canvasWidth : viewport.width * outputScale.sx | 0,
      canvasStyleHeight : viewport.height + 'px',
      canvasStyleWidth : viewport.width + 'px'
    }
  }

  waitForCanvasRefs(){
    return new Promise((resolve, reject) => {
      util.waitForCondition(() => {
        return _.keys(this.canvasRefs).length === this.state.pages.length;
      })
        .then(() => {
          resolve(true);
        })
    })
  }

  renderPage(pageIndex){
    let page = this.getPage(pageIndex);
    let canvasRef = this.canvasRefs[pageIndex];

    let ctx = canvasRef.getContext('2d', {
      alpha: false
    });

    let outputScale = PdfPage.getOutputScale(ctx);
    let viewport = page.getViewport({scale: 1}).clone({ scale : this.calculateViewportScale(outputScale) })
    this.pageSizes[pageIndex] = {
      canvasHeight : viewport.height * outputScale.sy | 0,
      canvasWidth : viewport.width * outputScale.sx | 0,
      canvasStyleHeight : viewport.height + 'px',
      canvasStyleWidth : viewport.width + 'px'
    }

    let drawViewport = viewport.clone({scale : this.calculateViewportScale(outputScale)});

    //Word to the wise...you CANNOT call ctx.scale() in here.
    //Doing this works SOMETIMES, but causes render problems on certain pdfs.  bug 1726
    //Instead, you're apparently supposed to use this undocumented method with this
    //transform property below.
    // I got that out of the pdf.js viewer code here - https://mozilla.github.io/pdf.js/web/viewer.html
    //I can't find any further documentation on this.
    let renderContext = {
      canvasContext: ctx,
      transform : !outputScale.scaled ? null : [outputScale.sx, 0, 0, outputScale.sy, 0, 0],
      viewport: drawViewport,
      intent: 'print'
    };

    return page.render(renderContext).promise;
  }

  setRef(index, ref) {
    this.canvasRefs[index] = ref;
  }

  render() {
    let {pages} = this.state;
    let { showing } = this.state;

    //I put d-none on the outside wrapper, but put the ref on the inner div.
    //The ref is what gets passed to the printer, so this style is not applied.
    return (
        <div className={'d-none'}>
          <div style={PdfPrintPreviewSvc.styles.wrap} ref={this.props.toPrintRef}>
            {showing && _.map(pages, (page) => {
                  let canvasSize = this.pageSizes[page.pageIndex];
                  if(!canvasSize){
                    return null;
                  }

                  return (
                      <div style={PdfPrintPreviewSvc.styles.page} key={page.pageIndex}>
                        <div style={PdfPrintPreviewSvc.styles.pageWrap}>
                          <div style={PdfPrintPreviewSvc.styles.canvasWrap}>
                            <canvas height={canvasSize.canvasHeight}
                                    width={canvasSize.canvasWidth}
                                    style={{
                                      width: '100%',
                                      height: '100%',
                                    }}
                                    ref={this.setRef.bind(this, page.pageIndex)}/>
                          </div>
                        </div>
                      </div>
                  )
                })}
          </div>
        </div>
    )
  }
}

PdfPrintPreviewSvc.styles = {
  wrap : {
    maxWidth: '100%',
  },
  page :{
    textAlign: 'center',
    backgroundColor: 'transparent',
    display: 'flex',
    justifyContent: 'center',
    maxWidth: '100%',
  },
  pageWrap:{
    position: 'relative',
    maxWidth: '100%',
  },
  canvasWrap:{
    height: 'inherit',
    width: 'inherit',
    maxWidth: '100%',
  }
}

const mapStateToProps = (state) => {
  return {}
}

const mapDispatchToProps = (dispatch) => {
  return {};
};

PdfPrintPreviewSvc.propTypes = {
    onRef: PropTypes.func.isRequired,
    toPrintRef: PropTypes.object.isRequired,
    sharedCanvasRef: PropTypes.object.isRequired
}

export default withVFTranslation()(connect(mapStateToProps, mapDispatchToProps)(PdfPrintPreviewSvc));
